import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { toast } from 'sonner';
import { Users, Activity, TrendingUp, UserPlus, BarChart3 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CorporateDashboard = () => {
  const { getAuthHeader } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [bulkEmployees, setBulkEmployees] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = getAuthHeader();
      const [dashRes, empRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/corporate/dashboard`, { headers }),
        axios.get(`${API}/corporate/employees`, { headers }),
        axios.get(`${API}/corporate/analytics`, { headers })
      ]);
      setDashboardData(dashRes.data);
      setEmployees(empRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkAdd = async (e) => {
    e.preventDefault();
    try {
      const lines = bulkEmployees.trim().split('\n');
      const employeeList = lines.map(line => {
        const [name, email, department] = line.split(',').map(s => s.trim());
        return { name, email, department: department || 'General' };
      }).filter(emp => emp.name && emp.email);

      await axios.post(
        `${API}/corporate/employees/bulk-add`,
        { employees: employeeList },
        { headers: getAuthHeader() }
      );
      
      toast.success(`${employeeList.length} employees added!`);
      setShowAddDialog(false);
      setBulkEmployees('');
      fetchData();
    } catch (error) {
      toast.error('Failed to add employees');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="container mx-auto px-6 lg:px-12 py-12" data-testid="corporate-dashboard">
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>
            {dashboardData?.corporate_account?.company_name}
          </h1>
          <p className="text-lg text-gray-600">Corporate Wellness Dashboard</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Users className="h-5 w-5 text-primary" />
                <span>Total Employees</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-primary">{dashboardData?.total_employees || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Activity className="h-5 w-5 text-primary" />
                <span>Assessments</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-primary">{dashboardData?.assessments_completed || 0}</div>
              <p className="text-sm text-gray-600">{dashboardData?.assessment_rate || 0}% completion</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <TrendingUp className="h-5 w-5 text-primary" />
                <span>Consultations</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-primary">{dashboardData?.total_consultations || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <BarChart3 className="h-5 w-5 text-primary" />
                <span>Wellness Score</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-primary">{dashboardData?.wellness_score || 0}%</div>
            </CardContent>
          </Card>
        </div>

        {/* Dosha Distribution */}
        {analytics && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Employee Dosha Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-6">
                {Object.entries(analytics.dosha_distribution).map(([dosha, count]) => (
                  <div key={dosha} className="text-center">
                    <div className="text-3xl font-bold text-primary capitalize">{count}</div>
                    <div className="text-sm text-gray-600 capitalize">{dosha}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Employee Management */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>Employee Directory</CardTitle>
              <Button onClick={() => setShowAddDialog(true)} data-testid="add-employees-btn">
                <UserPlus className="h-4 w-4 mr-2" />
                Add Employees
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {employees.slice(0, 10).map((emp) => (
                <div key={emp.id} className="flex justify-between items-center border-b pb-3 last:border-0">
                  <div>
                    <div className="font-medium">{emp.name}</div>
                    <div className="text-sm text-gray-600">{emp.email}</div>
                  </div>
                  <div className="text-sm text-gray-600">{emp.department || 'General'}</div>
                </div>
              ))}
              {employees.length === 0 && (
                <p className="text-center text-gray-500 py-8">No employees added yet</p>
              )}
            </div>
          </CardContent>
        </Card>
      </main>

      <Footer />

      {/* Add Employees Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl" data-testid="add-employees-dialog">
          <DialogHeader>
            <DialogTitle>Bulk Add Employees</DialogTitle>
            <p className="text-sm text-gray-600">Add employees in CSV format: Name, Email, Department (one per line)</p>
          </DialogHeader>

          <form onSubmit={handleBulkAdd} className="space-y-4">
            <div>
              <Label htmlFor="bulk-employees">Employees</Label>
              <textarea
                id="bulk-employees"
                className="w-full border border-gray-200 rounded-lg p-3 min-h-[300px] font-mono text-sm"
                value={bulkEmployees}
                onChange={(e) => setBulkEmployees(e.target.value)}
                placeholder="John Doe, john@company.com, Engineering\nJane Smith, jane@company.com, HR\nBob Wilson, bob@company.com, Sales"
                data-testid="bulk-employees-input"
              />
            </div>

            <div className="bg-muted p-4 rounded-lg text-sm">
              <p className="font-semibold mb-2">Default Password</p>
              <p className="text-gray-600">All employees will receive: <code className="bg-white px-2 py-1 rounded">Welcome@123</code></p>
              <p className="text-gray-600 mt-1 text-xs">They can change it after first login</p>
            </div>

            <Button type="submit" className="w-full" data-testid="submit-employees-btn">
              Add Employees
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CorporateDashboard;